<template>
  <ValidationObserver ref="internalInformationForm">
    <div v-if="isLoading">
      <v-skeleton-loader type="list-item" v-for="i in 10" :key="i" />
    </div>
    <div v-else>
      <v-row>
        <v-col class="col-md-6 input-item">
          <Permissioned :section="permissionsSection" vertical>
            <ValidationProvider
              permission="edit.cost-centre"
              v-slot="{ errors }"
              rules="notEmpty"
            >
              <Select
                height="50"
                v-model="internalInfo.client"
                placeholder="Start typing..."
                :error-messages="getErrors(errors)"
                :items="costCodes"
                :loading="isLoadingOrganisations"
                item-text="label"
                :value-comparator="compareBy('id')"
                :label="labelWithBudgetRemaining"
                @update:search="fetchFilteredOrganisations"
                autocomplete
                single-line
                clearable
              />
            </ValidationProvider>
          </Permissioned>
        </v-col>
        <v-col class="col-md-6 col-12 input-item text">
          <Permissioned :section="permissionsSection" vertical>
            <ValidationProvider
              permission="edit.vacancies"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                :error-messages="getErrors(errors)"
                @change="handleChange"
                label="Number of Vacancies"
                :min="1"
                type="number"
                v-model="internalInfo.vacancies"
              />
            </ValidationProvider>
          </Permissioned>
        </v-col>
        <v-col class="col-12 input-item">
          <Permissioned :section="permissionsSection" class="flex-column">
            <ValidationProvider
              permission="edit.reason-for-hire"
              v-slot="{ errors }"
              rules="required"
            >
              <Select
                v-model="internalInfo.reason"
                @change="handleChange"
                :items="reasons"
                :loading="isLoadingData"
                :error-messages="getErrors(errors)"
                item-text="reason"
                item-value="reason"
                label="Reason for Hire"
                single-line
                autocomplete
              />
            </ValidationProvider>
          </Permissioned>
        </v-col>
        <v-col class="col-12 input-item">
          <Permissioned :section="permissionsSection">
            <v-text-field
              permission="edit.apply-link-override"
              @change="handleChange"
              label="Apply Link"
              v-model="internalInfo.applyLinkOverride"
            />
          </Permissioned>
        </v-col>
        <v-col class="col-md-6 col-12 input-item">
          <v-text-field
            v-model="internalInfo.postNumber"
            @change="handleChange"
            label="Post Number"
          />
        </v-col>
        <v-col class="col-md-6 col-12 input-item">
          <ValidationProvider
            v-slot="{ errors }"
            :rules="shouldBeRequired('poNumber') ? 'required' : ''"
          >
            <v-text-field
              :error-messages="getErrors(errors)"
              v-model="internalInfo.poNumber"
              @change="handleChange"
              :label="
                shouldBeRequired('poNumber')
                  ? 'PO Number'
                  : 'PO Number (optional)'
              "
              placeholder=" "
            />
          </ValidationProvider>
        </v-col>
        <v-col class="col-12 input-item">
          <Permissioned :section="permissionsSection" class="flex-column">
            <v-checkbox
              permission="edit.internal-only"
              v-model="internalInfo.internalOnly"
              @change="handleChange"
              class="mt-0"
              label="Only advertise role internally"
            />
          </Permissioned>
        </v-col>
        <v-col class="col-12 input-item">
          <Permissioned :section="permissionsSection" class="flex-column">
            <v-checkbox
              permission="edit.anonymise-applications"
              v-model="internalInfo.anonymiseApplications"
              @change="handleChange"
              class="mt-0"
              label="Anonymise applications for this role"
            />
          </Permissioned>
        </v-col>
        <v-col class="col-12 input-item text">
          <Permissioned :section="permissionsSection" vertical>
            <v-text-field
              @change="handleChange"
              label="Maximum Applications"
              :min="1"
              type="number"
              v-model="internalInfo.maximumApplications"
            />
          </Permissioned>
        </v-col>
      </v-row>
    </div>
  </ValidationObserver>
</template>

<script>
import Select from "@/components/common/Select";
import { get, find, map, isEmpty, debounce } from "lodash";
import { createNamespacedHelpers } from "vuex";
import {
  GET_IS_FETCHING_ORGANISATIONS,
  GET_ORGANISATIONS
} from "@/store/modules/organisations/getters";
import { FETCH_ORGANISATIONS } from "@/store/modules/organisations/actions";
import { ORGANISATIONS_NAMESPACE } from "@/store/modules/organisations";
import { DATA_NAMESPACE } from "@/store/modules/data";
import {
  FETCH_REASONS_FOR_HIRE,
  FETCH_FIELD_NECESSITIES
} from "@/store/modules/data/actions";
import {
  GET_IS_FETCHING_DATA,
  GET_REASONS_FOR_HIRE,
  GET_FIELD_NECESSITIES
} from "@/store/modules/data/getters";
import { compareBy } from "@/utils/helpers";
import { getNumberAsMonetaryValue } from "@/utils/helpers";
import Permissioned from "@/components/common/Permissioned";
import { ValidationObserver, ValidationProvider } from "vee-validate";

const {
  mapActions: mapOrganisationActions,
  mapGetters: mapOrganisationGetters
} = createNamespacedHelpers(ORGANISATIONS_NAMESPACE);
const {
  mapActions: mapDataActions,
  mapGetters: mapDataGetters
} = createNamespacedHelpers(DATA_NAMESPACE);

const INIT_BASE_INTERNAL_INFORMATION_DATA = {
  client: {},
  poNumber: null,
  postNumber: null,
  applyLinkOverride: null,
  reason: "",
  vacancies: null,
  internalOnly: false,
  anonymiseApplications: 0,
  maximumApplications: null
};

export default {
  name: "InternalInformationSection",
  components: { Select, Permissioned, ValidationObserver, ValidationProvider },
  props: {
    initialValues: Object,
    bookingId: String,
    isUpdate: Boolean,
    validate: Boolean,
    initialTemplate: Object
  },
  data() {
    return {
      internalInfo:
        this.initialValues ||
        Object.assign({}, INIT_BASE_INTERNAL_INFORMATION_DATA),
      isLoading: false,
      showValidationErrors: false,
      organisationCurrentPage: 1
    };
  },
  async created() {
    this.isLoading = true;
    if (!isEmpty(this.initialTemplate)) {
      this.internalInfo.anonymiseApplications = this.initialTemplate.anonymiseApplications;
    }
    await Promise.all([
      this.fetchFieldNecessities({ filter: { form: "booking" } }),
      this.fetchHireReasons(this.organisationId),
      this.fetchFilteredOrganisations()
    ]);
    this.isLoading = false;
  },
  methods: {
    ...mapOrganisationActions({
      fetchOrganisations: FETCH_ORGANISATIONS
    }),
    ...mapDataActions({
      fetchHireReasons: FETCH_REASONS_FOR_HIRE,
      fetchFieldNecessities: FETCH_FIELD_NECESSITIES
    }),
    compareBy,
    getErrors(errors) {
      return this.showValidationErrors ? errors : [];
    },
    async isValid() {
      this.showValidationErrors = true;
      const valid = await this.$refs.internalInformationForm.validate();
      const { client, reason, poNumber } = this.internalInfo;
      const isPoNumberValid = !this.shouldBeRequired("poNumber") || poNumber;
      return !isEmpty(client) && reason && isPoNumberValid && valid;
    },
    handleChange() {
      this.$emit("change", this.internalInfo);
    },
    shouldBeRequired(field) {
      const fieldDetails = find(this.fieldNecessities, { field });
      if (fieldDetails) {
        return fieldDetails.required;
      }
      return false;
    },
    async fetchFilteredOrganisations(keyword = null) {
      await this.fetchOrganisations({
        filter: {
          has_cost_code: true,
          cost_code_search: keyword
        },
        page: {
          size: 50,
          number: this.organisationCurrentPage
        }
      });
    },
    endIntersect: debounce(function(entries, observer, isIntersecting) {
      if (isIntersecting) {
        this.organisationCurrentPage = this.organisationCurrentPage + 1;
        this.fetchFilteredOrganisations(this.keyword);
      }
    }, 800)
  },
  computed: {
    ...mapOrganisationGetters({
      organisations: GET_ORGANISATIONS,
      isLoadingOrganisations: GET_IS_FETCHING_ORGANISATIONS
    }),
    ...mapDataGetters({
      hireReasons: GET_REASONS_FOR_HIRE,
      isLoadingData: GET_IS_FETCHING_DATA,
      fieldNecessities: GET_FIELD_NECESSITIES
    }),
    reasons() {
      if (this.hireReasons.length) {
        return map(this.hireReasons, hireReason => {
          return { reason: hireReason.hireReason };
        });
      }
      return this.internalInfo.reason ? [this.internalInfo.reason] : [];
    },
    permissionsSection() {
      // component can be used for editing existing booking or creating the new one
      return this.bookingId ? `bookings[${this.bookingId}]` : "bookings";
    },
    units() {
      const { client } = this.internalInfo;
      if (!isEmpty(client)) {
        if (!find(this.organisations, { id: client.id })) {
          return [client, ...this.organisations];
        }
      }
      return this.organisations;
    },
    costCodes() {
      let costCodes = this.units.map(organisation => {
        organisation.label = `${
          organisation.parent ? organisation.parent.name + " -" : ""
        } ${organisation.name} (${organisation.costCode})`;
        return organisation;
      });
      if (costCodes.length) {
        return costCodes;
      }
      return [];
    },
    organisationId() {
      return get(this.$store, "state.auth.userData.organisation.id");
    },
    isDisabled() {
      return Boolean(isEmpty(this.internalInfo.client));
    },
    labelWithBudgetRemaining() {
      return this.internalInfo.client && this.internalInfo.client.totalBudget
        ? `Cost Code (Budget Remaining: 
              ${getNumberAsMonetaryValue(
                this.internalInfo.client.totalBudget,
                this.internalInfo.client.currencyCode
              )})`
        : "Cost Code";
    }
  },
  watch: {
    async validate(shouldValidate) {
      if (shouldValidate) {
        const isValid = await this.isValid();
        this.$emit(isValid ? "valid" : "invalid");
      }
    },
    initialValues(values) {
      this.internalInfo =
        values || Object.assign({}, INIT_BASE_INTERNAL_INFORMATION_DATA);
    }
  }
};
</script>

<style scoped lang="scss">
.input-item {
  padding-bottom: 0;
  padding-top: 0;

  &.text .v-input.v-text-field {
    margin-top: 22px;
  }
}
</style>
